import {assert} from 'Asserts';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import {PROJECT_TYPE, REGISTRY_TYPE} from 'constants/SourceConstants';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ApiIdPrefixToTypenameMapping,
  getPrefixToTypename,
  TYPENAME_MAPPING_FILENAME,
} from 'graphql-client/APIIDPrefixToTypename';
import {maybeGetTypenameFromID} from 'graphql-client/getTypenameFromID';
import {PARAMETER_TYPE_TO_CONFIG_TYPENAME} from 'insights/parameters/ParameterConstants';
import {ParameterType} from 'insights/parameters/ParameterTypes';
import {
  ALL_LEAF_PARTY_TYPES,
  PARTY_TYPE_TO_GRAPHQL_TYPENAME,
  PartyType,
  PartyTypename,
} from 'parties/PartyConstants';
import {ASSAY_SCHEMA_TYPE_TO_GQL_TYPENAME} from 'results/assay-schemas/AssaySchemaConstants';
import {Optional} from 'util/Types';

import {TranslatableValue} from './DeclarativeTranslator';
import {TYPENAME_RESOLVER_KEY} from './preprocessedData';

const INACCESSIBLE_INFO_TYPENAME = 'InaccessibleInfoNode';

export abstract class BaseTypenameResolver {
  public abstract getTypenameFromID(restID: string): string;

  public abstract getTypenameFromObj(restObj: TranslatableValue): string;
}

// Exported for tests - prefer getDefaultTypenameResolver instead
export class DefaultTypenameResolver extends BaseTypenameResolver {
  apiIDPrefixToTypename: ApiIdPrefixToTypenameMapping;

  constructor(apiIDPrefixToTypename: ApiIdPrefixToTypenameMapping) {
    super();
    this.apiIDPrefixToTypename = apiIDPrefixToTypename;
  }

  public getTypenameFromID(nullableRESTID: Optional<string | number>): string {
    const typenameFromAPIIDPrefix = maybeGetTypenameFromID(this.apiIDPrefixToTypename, nullableRESTID);
    assert(
      typenameFromAPIIDPrefix != null,
      `Could not compute typename for ID ${nullableRESTID} - consider adding a record to ${TYPENAME_MAPPING_FILENAME} ` +
        `or adding a ${TYPENAME_RESOLVER_KEY}`
    );
    return typenameFromAPIIDPrefix;
  }

  public getTypenameFromObj(restObj: TranslatableValue): string {
    // @ts-expect-error
    const id: Optional<string | number> = restObj.id;
    const typenameFromAPIIDPrefix = maybeGetTypenameFromID(this.apiIDPrefixToTypename, id);
    if (typenameFromAPIIDPrefix) {
      return typenameFromAPIIDPrefix;
    }

    if (isInaccessibleInfo(restObj)) {
      return INACCESSIBLE_INFO_TYPENAME;
    }

    throw new Error(
      `Could not compute typename for restObj with ID ${id} - consider adding a record to ` +
        `${TYPENAME_MAPPING_FILENAME} or adding a ${TYPENAME_RESOLVER_KEY}.\n\nREST object: ${JSON.stringify(restObj)}`
    );
  }
}

export function getDefaultTypenameResolver(): DefaultTypenameResolver {
  return new DefaultTypenameResolver(getPrefixToTypename());
}

class ConstantTypenameResolver extends BaseTypenameResolver {
  // Used for situations in which the system can't infer the typename (e.g. because the ID is an integer) but
  // the context makes it obvious what the typename should be.

  typename: string;

  constructor(typename: string) {
    super();
    this.typename = typename;
  }

  public getTypenameFromID(restID: string): string {
    return this.typename;
  }

  public getTypenameFromObj(restObj: TranslatableValue): string {
    return this.typename;
  }
}

class SourceTypenameResolver extends BaseTypenameResolver {
  public getTypenameFromID(restID: string): string {
    throw new Error('Cannot infer Source __typename from just an ID');
  }

  public getTypenameFromObj(restObj: TranslatableValue): string {
    if (isInaccessibleInfo(restObj)) {
      return INACCESSIBLE_INFO_TYPENAME;
    }

    // @ts-expect-error Accessing key on unknown object
    const sourceType: PROJECT_TYPE | REGISTRY_TYPE = restObj.sourceType;
    assert(
      [PROJECT_TYPE, REGISTRY_TYPE].includes(sourceType),
      `SourceTypenameResolver: Unexpected sourceType ${sourceType}`
    );
    return sourceType === PROJECT_TYPE ? 'ProjectNode' : 'RegistryNode';
  }
}

class AnalyticsParameterConfigTypenameResolver extends BaseTypenameResolver {
  public getTypenameFromID(restID: string): string {
    throw new Error('Cannot infer AnalyticsParameterConfig __typename from just an ID');
  }

  public getTypenameFromObj(restObj: TranslatableValue): string {
    if (isInaccessibleInfo(restObj)) {
      return INACCESSIBLE_INFO_TYPENAME;
    }
    return PARAMETER_TYPE_TO_CONFIG_TYPENAME[restObj.type as ParameterType];
  }
}

class AssaySchemaTypenameResolver extends BaseTypenameResolver {
  public getTypenameFromID(restID: string): string {
    throw new Error('Cannot infer AssaySchema __typename from just an ID');
  }

  public getTypenameFromObj(restObj: TranslatableValue): string {
    if (isInaccessibleInfo(restObj)) {
      return INACCESSIBLE_INFO_TYPENAME;
    }
    return ASSAY_SCHEMA_TYPE_TO_GQL_TYPENAME[restObj.schemaType as LegacyAssaySchemaType];
  }
}

class PartyTypenameResolver extends BaseTypenameResolver {
  public getTypenameFromID(restID: string): string {
    throw new Error('Cannot infer party __typename from just an ID');
  }

  public getTypenameFromObj(restObj: TranslatableValue): PartyTypename | 'InaccessibleInfoNode' {
    if (isInaccessibleInfo(restObj)) {
      return INACCESSIBLE_INFO_TYPENAME;
    }

    assert(
      ALL_LEAF_PARTY_TYPES.includes(restObj.type as PartyType),
      `PartyTypenameResolver: Unexpected party type ${restObj.type}`
    );

    return PARTY_TYPE_TO_GRAPHQL_TYPENAME[restObj.type as PartyType];
  }
}

const KEY_TO_TYPENAME_RESOLVER: {[key: string]: BaseTypenameResolver} = {
  AssaySchemaTypenameResolver: new AssaySchemaTypenameResolver(),
  AnalyticsParameterConfigTypenameResolver: new AnalyticsParameterConfigTypenameResolver(),
  ArchiveRecordTypenameResolver: new ConstantTypenameResolver('ArchiveRecordNode'),
  AssayRecordOriginTypenameResolver: new ConstantTypenameResolver('AssayRecordOriginNode'),
  AutomationInputFileConfigTypenameResolver: new ConstantTypenameResolver('AutomationInputFileConfigNode'),
  AutomationOutputFileConfigTypenameResolver: new ConstantTypenameResolver('AutomationOutputFileConfigNode'),
  AutomationOutputFileDestinationInfoTypenameResolver: new ConstantTypenameResolver(
    'AutomationOutputFileDestinationInfoNode'
  ),
  AutomationOutputFileResultSchemaInfoTypenameResolver: new ConstantTypenameResolver(
    'AutomationOutputFileResultSchemaInfoNode'
  ),
  AutomationOutputFileTagSchemaInfoTypenameResolver: new ConstantTypenameResolver(
    'AutomationOutputFileTagSchemaInfoNode'
  ),
  ComponentLotValuesTypenameResolver: new ConstantTypenameResolver('ComponentLotValuesNode'),
  ContainerContentTypenameResolver: new ConstantTypenameResolver('ContainerContentNode'),
  EntryDayTypenameResolver: new ConstantTypenameResolver('EntryDayNode'),
  EquipmentTypenameResolver: new ConstantTypenameResolver('EquipmentNode'),
  ExperimentalRoleTypenameResolver: new ConstantTypenameResolver('ExperimentalRoleNode'),
  FolderTypenameResolver: new ConstantTypenameResolver('FolderNode'),
  GridCellTypenameResolver: new ConstantTypenameResolver('GridCellNode'),
  MixtureSchemaConfigurationTypenameResolver: new ConstantTypenameResolver('MixtureSchemaConfigurationNode'),
  ModifiedNucleotideTypenameResolver: new ConstantTypenameResolver('ModifiedNucleotideNode'),
  NameTemplateTypenameResolver: new ConstantTypenameResolver('NameTemplateNode'),
  NameTemplatePartTypenameResolver: new ConstantTypenameResolver('NameTemplatePartNode'),
  PartyTypenameResolver: new PartyTypenameResolver(),
  PermissionsTypenameResolver: new ConstantTypenameResolver('PermissionsNode'),
  ProjectTypenameResolver: new ConstantTypenameResolver('ProjectNode'),
  RegistryFileNamePartTypenameResolver: new ConstantTypenameResolver('RegistryFileNamePartNode'),
  RegistryTypenameResolver: new ConstantTypenameResolver('RegistryNode'),
  RequestSchemaTypenameResolver: new ConstantTypenameResolver('RequestSchemaNode'),
  ResultBlobTypenameResolver: new ConstantTypenameResolver('ResultBlobNode'),
  ResultSchemaTypenameResolver: new ConstantTypenameResolver('ResultSchemaNode'),
  ResultTypenameResolver: new ConstantTypenameResolver('ResultNode'),
  ReviewLifecycleVersionResolver: new ConstantTypenameResolver('ReviewLifecycleVersionNode'),
  RunSchemaTypenameResolver: new ConstantTypenameResolver('RunSchemaNode'),
  RunTypenameResolver: new ConstantTypenameResolver('RunNode'),
  SampleRolesTypenameResolver: new ConstantTypenameResolver('SampleRolesNode'),
  SchemaInterfaceTypenameResolver: new ConstantTypenameResolver('SchemaInterfaceNode'),
  SourceTypenameResolver: new SourceTypenameResolver(),
  TagSchemaConstraintTypenameResolver: new ConstantTypenameResolver('TagSchemaConstraintNode'),
  TagSchemaNodeTypenameResolver: new ConstantTypenameResolver('TagSchemaNode'),
  TagSchemaValidationErrorLabelSummaryTypenameResolver: new ConstantTypenameResolver(
    'TagSchemaValidationErrorLabelSummaryNode'
  ),
  TagSchemaValidationStatusSummaryTypenameResolver: new ConstantTypenameResolver(
    'TagSchemaValidationStatusSummaryNode'
  ),
  UserTypenameResolver: new ConstantTypenameResolver('UserNode'),
  QueryTypenameResolver: new ConstantTypenameResolver('Query'),
};

export function getTypenameResolver(nullableTypenameResolverName: string | null): BaseTypenameResolver {
  assert(
    nullableTypenameResolverName == null ||
      KEY_TO_TYPENAME_RESOLVER.hasOwnProperty(nullableTypenameResolverName),
    `Expected to find typename resolver ${nullableTypenameResolverName} in KEY_TO_TYPENAME_RESOLVER`
  );
  return KEY_TO_TYPENAME_RESOLVER[nullableTypenameResolverName!] ?? getDefaultTypenameResolver();
}
